import { useRef, useCallback } from 'react';
import { findNearestScroller, clamp } from '@utils/common';
import { CSSRefTransition } from './transition.css.js';
import { jsx } from 'react/jsx-runtime';

/**
 * acts similarily to the base <Transition /> component (which this component
 * actually builds upon), with the addition that `width` and `height`
 * properties can be transitioned, which is not natively supported by browsers.
 *
 * by default components applying this transition will grow from 0 to their
 * original size in the specified axis when entering and the opposite when
 * exiting.
 *
 * NB: The `transition` property MUST be declared on the HTMLElement that is
 * wrapped with the <SizeTransition /> with a value including the specified
 * axis.
 *
 * @example
 * ```
 * const contentCls = css`
 *  &-enter-active {
 *    transition: height 200ms linear;
 *  }
 *
 *  &-exit-active {
 *    transition: height 200ms linear;
 *  }
 * `
 *
 * <SizeTransition
 *  in={active}
 *  axis="horisontal"
 *  timeout={200}
 *  classNames={contentCls}
 * >
 *  {(ref) => <div ref={ref} className={contentCls} />}
 * </SizeTransition>
 * ```
 */
function CollapsibleTransition({
  axis,
  skipValidation,
  retainElementPositionDuringAnimation,
  children,
  getMinSizeInPx,
  getMaxSizeInPx,
  strategy,
  ...props
}) {
  const lastEventRef = useRef(undefined);
  const internalRef = useRef(null);
  const nodeRef = props.nodeRef ?? internalRef;
  const elementPositionAnimationFrameRef = useRef(0);
  const onEnter = useCallback(() => {
    lastEventRef.current = "enter";
    const element = nodeRef.current;
    if (!element) {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element);

    // ... else apply the size on the element itself as default
    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constraints.minSizeInPx.width}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constraints.minSizeInPx.height}px`;
    }
    if (retainElementPositionDuringAnimation) {
      const initialPosition = element.getBoundingClientRect().top;
      const scroller = findNearestScroller(element);
      const fixateElementPosition = () => {
        elementPositionAnimationFrameRef.current = window.requestAnimationFrame(fixateElementPosition);
        const currentPosition = element?.getBoundingClientRect().top;
        if (!currentPosition) {
          return;
        }
        const diff = currentPosition - initialPosition;
        if (scroller && retainElementPositionDuringAnimation !== "window") {
          scroller.scrollTop += diff;
        } else {
          window.scrollTo({
            left: window.scrollX,
            top: window.scrollY + diff,
            behavior: "auto"
          });
        }
      };
      elementPositionAnimationFrameRef.current = window.requestAnimationFrame(fixateElementPosition);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeRef, axis, getMinSizeInPx, getMaxSizeInPx]);
  const onEntering = useCallback(() => {
    lastEventRef.current = "enter-active";
    const element = nodeRef.current;
    if (!element) {
      return;
    }

    // slightly defer running the enter animation, which provides enough time
    // for certain components to properly mount and apply size calculations (e.g
    // a component, which can possibly apply height limitations within a layout
    // effect)
    if (lastEventRef.current !== "enter-active") {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element);
    const constrainedHeight = clamp(constraints.minSizeInPx.height, strategy === "clientSize" ? element.clientHeight : element.scrollHeight, constraints.maxSizeInPx.height);
    const constrainedWidth = clamp(constraints.minSizeInPx.width, strategy === "clientSize" ? element.clientWidth : element.scrollWidth, constraints.maxSizeInPx.width);

    // ... else apply the size on the element itself as default
    validateElement(element, {
      axis,
      skipValidation
    });
    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constrainedWidth}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constrainedHeight}px`;
    }
  }, [nodeRef, getMaxSizeInPx, getMinSizeInPx, strategy, axis, skipValidation]);
  const onEntered = useCallback(() => {
    window.cancelAnimationFrame(elementPositionAnimationFrameRef.current);
    lastEventRef.current = "enter-done";
    const element = nodeRef.current;
    const parent = element?.parentElement;
    if (!element || !parent) {
      return;
    }
    const target = element;
    if (["both", "horisontal"].includes(axis)) {
      target.style.width = "";
    }
    if (["both", "vertical"].includes(axis)) {
      target.style.height = "";
    }
  }, [nodeRef, axis]);
  const onExit = useCallback(() => {
    window.cancelAnimationFrame(elementPositionAnimationFrameRef.current);
    lastEventRef.current = "exit";
    const element = nodeRef.current;
    if (!element) {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element);
    const constrainedHeight = clamp(constraints.minSizeInPx.height, strategy === "clientSize" ? element.clientHeight : element.scrollHeight, constraints.maxSizeInPx.height);
    const constrainedWidth = clamp(constraints.minSizeInPx.width, strategy === "clientSize" ? element.clientWidth : element.scrollWidth, constraints.maxSizeInPx.width);
    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constrainedWidth}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constrainedHeight}px`;
    }
  }, [nodeRef, getMaxSizeInPx, getMinSizeInPx, strategy, axis]);
  const onExiting = useCallback(() => {
    lastEventRef.current = "exit-active";
    const element = nodeRef.current;
    if (!element) {
      return;
    }

    // slightly defer running the exit animation, which provides enough time
    // for certain components to properly mount and apply size calculations (e.g
    // a component, which can possibly apply height limitations within a layout
    // effect)
    if (lastEventRef.current !== "exit-active") {
      return;
    }
    const constraints = getComputedConstraints({
      getMaxSizeInPx,
      getMinSizeInPx
    }, element);
    validateElement(element, {
      axis,
      skipValidation
    });
    if (["both", "horisontal"].includes(axis)) {
      element.style.width = `${constraints.minSizeInPx.width}px`;
    }
    if (["both", "vertical"].includes(axis)) {
      element.style.height = `${constraints.minSizeInPx.height}px`;
    }
  }, [nodeRef, getMaxSizeInPx, getMinSizeInPx, axis, skipValidation]);
  const onExited = useCallback(() => {
    lastEventRef.current = "exit-done";

    // @ts-expect-error <TransitionGroup /> will provide an onExited callback
    // which we need to manually execute once the transition has been fully
    // completed.
    // This callback is however NOT typed in our props as we do not want
    // integrations to provide their own onExited callback.
    props.onExited?.();
    // @ts-expect-error See description above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onExited]);
  return /*#__PURE__*/jsx(CSSRefTransition, {
    ...props,
    nodeRef: nodeRef,
    onEnter: onEnter,
    onEntering: onEntering,
    onEntered: onEntered,
    onExit: onExit,
    onExiting: onExiting,
    onExited: onExited,
    children: children
  });
}

/**
 * In case size constraints has been applied to the element which can be
 * calculated in pixels (or directly provided to this transition) then ensure
 * we limit the transition to these constraints.
 */
function getComputedConstraints(args, element) {
  const computedStyles = window.getComputedStyle(element);
  const boundingBox = element.getBoundingClientRect();
  const minHeight = computedStyles.minHeight === "none" || !computedStyles.minHeight.includes("px") ? 0 : parseFloat(computedStyles.minHeight);
  const maxHeight = computedStyles.maxHeight === "none" || !computedStyles.maxHeight.includes("px") ? window.innerHeight - boundingBox.top : Math.min(parseFloat(computedStyles.maxHeight), window.innerHeight - boundingBox.top);
  const minWidth = computedStyles.minWidth === "none" || !computedStyles.minWidth.includes("px") ? 0 : parseFloat(computedStyles.minWidth);
  const maxWidth = computedStyles.maxWidth === "none" || !computedStyles.maxWidth.includes("px") ? window.innerWidth - boundingBox.left : Math.min(parseFloat(computedStyles.maxWidth), window.innerWidth - boundingBox.left);
  const explicitMinSize = args.getMinSizeInPx?.();
  const explicitMaxSize = args.getMaxSizeInPx?.();
  return {
    minSizeInPx: {
      width: Math.max(minWidth, explicitMinSize?.width ?? 0),
      height: Math.max(minHeight, explicitMinSize?.height ?? 0)
    },
    maxSizeInPx: {
      width: Math.min(maxWidth, explicitMaxSize?.width ?? Infinity),
      height: Math.min(maxHeight, explicitMaxSize?.height ?? Infinity)
    }
  };
}

/**
 * observes the provided element and ensures that it has all the CSS properties
 * required to properly integrate with the <SizeTransition />.
 */
function validateElement(element, args) {
  if (process.env["NODE_ENV"] === "production" || args.skipValidation) {
    return;
  }
  const computedStyles = getComputedStyle(element);
  const hasValidHeightTransition = computedStyles.transition !== "all 0s ease 0s" && (computedStyles.transition.includes("all") || computedStyles.transition.includes("height"));
  const hasValidWidthTransition = computedStyles.transition !== "all 0s ease 0s" && (computedStyles.transform.includes("all") || computedStyles.transition.includes("width"));
  const elementLabel = "transitioning element";
  if (!hasValidHeightTransition && ["both", "vertical"].includes(args.axis)) {
    console.warn(`<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'height' property`, element);
  }
  if (!hasValidWidthTransition && ["both", "horisontal"].includes(args.axis)) {
    console.warn(`<SizeTransition />: The ${elementLabel} must have a CSS 'transition' declaration including the 'width' property`, element);
  }
}

export { CollapsibleTransition };
//# sourceMappingURL=transition.collapsible.js.map
